import { type FC, useRef, Fragment, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useBuyerProductTileSize } from '../../shared/hooks/useBuyerProductTileSize';
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { TILE_SIZES, TILE_SIZES_PX } from 'dibs-buyer-product-tile/exports/BuyerProductTile';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { type SbSharedPartialItemsPlaceholder_itemSearch$key } from './__generated__/SbSharedPartialItemsPlaceholder_itemSearch.graphql';

type Props = {
    itemSearch?: SbSharedPartialItemsPlaceholder_itemSearch$key;
    fetchPartialItems: () => void;
    numOfInflatedItems: number;
    numOfPlaceholderItems: number;
    numOfAboveFoldRows?: number;
};

const itemSearchFragment = graphql`
    fragment SbSharedPartialItemsPlaceholder_itemSearch on ItemSearchQueryConnection {
        ...SbSharedProductAdWrapper_itemSearch
    }
`;

export const SbSharedPartialItemsPlaceholder: FC<Props> = ({
    itemSearch: itemSearchRef = undefined,
    fetchPartialItems,
    numOfInflatedItems,
    numOfPlaceholderItems,
    numOfAboveFoldRows,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const partialItemsVisibilityRef = useRef<HTMLDivElement>(null);
    const [hasError, setHasError] = useState(false);
    const tileSize = useBuyerProductTileSize();
    const tileSizePx = TILE_SIZES_PX[tileSize];
    const itemsPerRow = tileSize === TILE_SIZES.small ? 2 : 3;
    const numOfAboveFoldRowsToUse = numOfAboveFoldRows || (tileSize === TILE_SIZES.small ? 2 : 3);
    const pageCol = tileSize === TILE_SIZES.small ? 2 : undefined;

    if (!numOfPlaceholderItems) {
        return null;
    }

    // Want to show Error page when failed to inflate items
    if (hasError) {
        throw new Error('Failed to inflate items');
    }

    return (
        <>
            {Array.from({ length: numOfPlaceholderItems }).map((_, index) => (
                <Fragment key={`partialItem-${index}`}>
                    {itemSearch && (
                        <SbSharedProductAdWrapper
                            // finding original item index
                            index={numOfInflatedItems + index}
                            itemSearch={itemSearch}
                            pageCol={pageCol}
                            isPlaceholder
                        />
                    )}
                    <BuyerProductTileShimmer
                        size={tileSize}
                        visibilityRef={index === 0 ? partialItemsVisibilityRef : undefined}
                    />
                </Fragment>
            ))}
            <VisibilityTracker
                elementRef={partialItemsVisibilityRef}
                observerOptions={{
                    // (${numOfInflatedItems} / ${itemsPerRow} - ${numOfAboveFoldRowsToUse}) * ${tileSizePx} + 200px to account for other stuff on the top of the page
                    rootMargin: `${
                        Math.ceil(numOfInflatedItems / itemsPerRow - numOfAboveFoldRowsToUse) *
                            tileSizePx +
                        200
                    }px 0px`,
                }}
                onVisibilityChange={async ({ isVisible, boundingClientRect, disconnect }) => {
                    // boundingClientRect.bottom check is for cases when page is refreshed somewhere below the visibility tracker element
                    if (isVisible || boundingClientRect.bottom < 0) {
                        disconnect();
                        // can't just throw an error as an intersection observer is outside the React tree and it's not catched by error boundary
                        try {
                            await fetchPartialItems();
                        } catch (error) {
                            setHasError(true);
                        }
                    }
                }}
            />
        </>
    );
};
